/**
 * riskGradeVocabulary — the one Hebrew vocabulary for the security_policy risk GRADE.
 *
 * This is the 0–3 integer scale declared in `backend/app/security_policy.py` §5 as the four
 * `RISK_*` constants — `RISK_READ_ONLY` 0, `RISK_LOW_REMEDIATION` 1, `RISK_SENSITIVE` 2,
 * `RISK_CRITICAL_TIER0` 3 — and it is the grade that decides what an action needs before it may
 * run: level 0 needs no approval, level 1 a standard session, level 2 *strong* human approval
 * (Windows Hello / FIDO2 / Passkey), level 3 a 2-of-3 approval. Two producers reach the frontend
 * with it:
 *
 *   - `ApprovalToken.risk_level` (`backend/app/models/approval_token.py`, `Integer`, default 0).
 *     Three paths mint a token and none of them grades per action: `agent_runs._TIER_RISK` derives
 *     it from the agent's permission tier (`tier_1`→0, `tier_2`→1, `tier_3`→2, anything else→0), the
 *     operator-channel remote session binds the registry entry's own arm (`run_diagnostic_script`,
 *     which is 1), and the browser work executor uses 2 for a state-changing action and 0 otherwise.
 *     Arms 0–2 are therefore what a token can carry.
 *   - `Tool.risk_level` (`backend/app/services/tool_registry.py`), which *does* mint all four arms —
 *     the read-only probes are 0 and the execute/cross-tenant tools are 3 — and whose constructor
 *     refuses anything outside 0–3. `GET /api/agents/tools` returns the whole registry, so arm 3
 *     reaches the Agent Builder's tool list; the System Map's tool panel and the Connections tool
 *     table read `GET /api/connections/health`, which returns only the two read-only pilot probes,
 *     so 0 is what those two show today.
 *
 * The scale is therefore the same closed four-arm scale in both producers — and the map below holds
 * all four arms for that reason, even though no token can carry arm 3: a *tool* can. The file exists
 * because the frontend did not treat the scale as one. Every site below words a *different* scale
 * with the same word "risk": `AnalysisOutput.risk_level` and `AgentAction.risk_level` are the
 * green/amber/red strings ("low"/"medium"/"high" in Hebrew), and this one is an integer. The
 * integer was read through the string maps, which cannot hold it, so:
 *
 *   - `SystemMapLivePage`'s tool row ran the integer through `riskLabel` and printed "לא ידוע"
 *     (unknown) for every tool, so a read-only tool's grade was unreadable;
 *   - `TicketEmployeeWorkbench` and `AgentRunDetailPage` fell through to the raw digit, so a human
 *     approval of a sensitive action read `סיכון 2` — and the workbench gated the whole segment on
 *     truthiness, so arm 0, the grade `_TIER_RISK` mints for a tier-1 agent, showed no grade at all;
 *   - `ConnectionsPage` printed the bare digit in its Risk column;
 *   - `ticketCoworkerModel.riskLabel` answered a digit with `רמת סיכון לא ידועה: 2`.
 *
 * Two rules this module exists to hold, the same two `runStatusVocabulary` holds:
 *
 *   1. One scale, re-derived from the producer. `riskGradeVocabulary.test.js` parses the `RISK_*`
 *      values out of the Python source and asserts this map's key set EQUALS them, and parses the
 *      range `tool_registry` validates, so a fifth level or a renumbered constant reddens the suite
 *      instead of shipping a digit to an operator.
 *   2. An unrecognised value is NAMED, never echoed — the reason `riskGradeLabel` will not put a
 *      digit on screen. A value outside the scale means this build is behind the producer.
 *
 * Wording. The grade words are bare (`רגיש`, not `סיכון: רגיש`): this map is read inside a chip
 * label, after a `סיכון ` prefix and alone in a risk column, so the surrounding frame belongs to
 * the call site. `רגיש`/`קריטי`/`קריאה בלבד` are the words two of the three deleted page-local maps
 * already used and the ones the Remote Operations and Employee Builder suites pin, so only the
 * frames change.
 *
 * WHAT THIS FILE CANNOT SEE, stated so the coverage claim cannot go short. `riskGradeLabel` words
 * the *grade*; it does not word `Tool.operation_type` (`read_only`/`write`/…), a different field on
 * the same Connections row with its own producer — that one is `operationTypeVocabulary`'s, which
 * is why it is no longer printed raw there — nor the green/amber/red maps this module deliberately
 * leaves alone — `RISK_LABELS` in `ticketCoworkerModel`, `TicketEmployeeWorkbench`, `AgentRunPanel`,
 * `ApprovalCenterPage`, `SystemMapLivePage` and `RISK_HE` in `ScheduledOperationsCenterPage` are
 * indexed by an *analysis* or *action* risk, which is that other scale's business, not this one's.
 * `AgentRunDetailPage`'s badge colours are an inline ternary on the same digits, listed here because
 * they look like this module's business and are not: that ternary already covers all four arms, so
 * there is nothing on it to move.
 */

/**
 * The four arms of the `security_policy` risk grade, by integer value.
 *
 * Keys are numbers and are looked up with a value that may be a JSON string ("2"), which
 * `hasOwnProperty` coerces — see `riskGradeLabel`.
 */
export const RISK_GRADE_HE = {
  0: "קריאה בלבד",
  1: "נמוך",
  2: "רגיש",
  3: "קריטי",
};

/** Shown for a value the scale does not hold. Never the number itself. */
export const RISK_GRADE_UNKNOWN_HE = "דרגת סיכון לא מזוהה";

/**
 * The Hebrew words for a stored risk grade. `hasOwnProperty` rather than `RISK_GRADE_HE[value] ||`:
 * a bare lookup answers an inherited key (`"constructor"`, `"toString"`) with a function, which a
 * `||` fallback would treat as a hit and render.
 *
 * `null`/`undefined` and a value on the *other* risk scale (`"amber"`) both land on the named
 * unknown rather than on a digit or an empty string, so a caller cannot print a grade it does not
 * have.
 */
export function riskGradeLabel(value) {
  return Object.prototype.hasOwnProperty.call(RISK_GRADE_HE, value)
    ? RISK_GRADE_HE[value]
    : RISK_GRADE_UNKNOWN_HE;
}
